import React, { useEffect, useState } from "react";
import ScheduleTable from "./ScheduleTable";
import ScheduleForm from "./ScheduleForm";
import ScrollToTopButton from "./ScrollToTopButton";
import { Button } from "../components/ui/PressableButton";
import FadeIn from "../components/ui/FadeIn";

const AdminPanel = () => {
  const [isAmazonConnected, setIsAmazonConnected] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [user, setUser] = useState(null);

  const token = localStorage.getItem("token");

  const handleAuthError = (res) => {
    if (res.status === 401) {
      alert("ログインセッションが切れました。再ログインしてください。");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return true;
    }
    return false;
  };

  const fetchCampaigns = async () => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (handleAuthError(res)) return;

      if (!res.ok) {
        console.error("キャンペーン取得失敗");
        return;
      }

      const data = await res.json();
      console.log("APIレスポンス:", data);

      setIsAmazonConnected(data.isAmazonConnected);

      const raw = Array.isArray(data.campaigns) ? data.campaigns : Array.isArray(data) ? data : [];

      console.log("raw[0]:", raw[0]);

      const filtered = raw.filter((c) => !c.state || c.state !== "ARCHIVED");

      console.log("📦 raw.length:", raw.length);
      console.log("✅ filtered.length (非ARCHIVED):", filtered.length);
      console.log("🔍 filtered[0]:", filtered[0]);

      const list = filtered
        .filter((c) => (c.name || c.campaignName) && c.campaignId)
        .map((c) => {
          const rawMode = String(c.mode || "").toLowerCase();
          const mode =
            rawMode === "auto" || rawMode === "manual"
              ? rawMode
              : c.targetingType === "AUTO"
              ? "auto"
              : c.targetingType === "MANUAL"
              ? "manual"
              : "unknown";

          return {
            id: c.campaignId || c.id,
            name: c.name || c.campaignName,
            mode,
            targetingType: c.targetingType,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      console.log("最終リスト:", list);
      console.log("list sample:", list.slice(0, 5));
      console.log("raw vs list 件数:", { raw: filtered.length, list: list.length, dropped: filtered.length - list.length });
      console.log(
        "raw targetingType/mode sample:",
        filtered.slice(0, 5).map((c) => ({
          campaignId: c.campaignId,
          campaignIdType: typeof c.campaignId,
          targetingType: c.targetingType,
          mode: c.mode,
          name: c.name || c.campaignName,
        }))
      );
      setCampaigns(list);
    } catch (err) {
      console.error("❌ 通信エラー（campaigns）:", err.message);
    }
  };

  const fetchSchedules = async () => {
    const token = localStorage.getItem("token");

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schedules`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (handleAuthError(res)) return;

    if (!res.ok) {
      console.error("スケジュール取得失敗");
      return;
    }

    let data = await res.json();
    data = data.sort((a, b) => {
      if (a.campaignId < b.campaignId) return -1;
      if (a.campaignId > b.campaignId) return 1;
      if (a.mode < b.mode) return -1;
      if (a.mode > b.mode) return 1;
      if ((a.hour ?? 0) !== (b.hour ?? 0)) return (a.hour ?? 0) - (b.hour ?? 0);
      return (a.minute ?? 0) - (b.minute ?? 0);
    });
    setSchedules(data);
  };

  useEffect(() => {
    fetchSchedules();
    fetchCampaigns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (handleAuthError(res)) return;

        if (res.ok) {
          const data = await res.json();
          setUser(data);
          console.log("🧑 ユーザー情報:", data);
        } else {
          console.warn("⚠️ ユーザー情報取得失敗");
        }
      } catch (err) {
        console.error("❌ ユーザー取得エラー:", err.message);
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    let timeoutId;
    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        alert("30分間操作がなかったためログアウトしました。");
        localStorage.removeItem("token");
        window.location.href = "/login";
      }, 30 * 60 * 1000);
    };

    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, []);

  const handleCreate = async (formData) => {
    const payload = {
      campaignId: formData.campaignId,
      campaignName: formData.campaignName,
      state: formData.state,
      budget: formData.budget ? Number(formData.budget) : undefined,
      mode: formData.mode,
      hour: formData.mode === "daily" ? Number(formData.hour) : undefined,
      minute: formData.mode === "daily" ? Number(formData.minute) : undefined,
      weekdays: formData.mode === "daily" ? formData.weekdays : undefined,
      scheduledDateTime: formData.mode === "once" ? formData.scheduledDateTime : undefined,
    };

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schedules`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      alert("✅ 登録成功");
      fetchSchedules();
    } else {
      const error = await res.json();
      alert(`❌ 登録失敗: ${error.message}`);
    }
  };

  const handleUpdate = async (formData) => {
    const payload = {
      campaignId: formData.campaignId,
      state: formData.state,
      budget: formData.budget ? Number(formData.budget) : undefined,
      mode: formData.mode,
      hour: formData.mode === "daily" ? Number(formData.hour) : undefined,
      minute: formData.mode === "daily" ? Number(formData.minute) : undefined,
      weekdays: formData.mode === "daily" ? formData.weekdays : undefined,
      scheduledDateTime: formData.mode === "once" ? formData.scheduledDateTime : undefined,
    };

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schedules/${editing._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      alert("✅ 更新成功");
      setEditing(null);
      fetchSchedules();
    } else {
      const error = await res.json();
      alert(`❌ 更新失敗: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("本当に削除しますか？")) return;
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schedules/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      fetchSchedules();
    } else {
      const error = await res.json();
      alert(`❌ 削除失敗: ${error.message}`);
    }
  };

  const handleToggleEnabled = async (schedule) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schedules/${schedule._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ enabled: !schedule.enabled }),
    });
    if (res.ok) {
      fetchSchedules();
    } else {
      const error = await res.json();
      alert(`❌ 更新失敗: ${error.message}`);
    }
  };

  return (
    <FadeIn duration={380} offsetY={8}>
      <div style={{ maxWidth: "800px", margin: "auto", padding: "16px" }}>
        {/* ヘッダーは少し早めに */}
        <FadeIn delay={40}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img src="/icon.png" alt="アイコン" style={{ width: "36px", height: "36px" }} />
              <h1 style={{ margin: 0 }}>AdSche（アドスケ）</h1>
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              {/* 使い方 / お問い合わせ */}
              <div style={{ display: "flex", gap: "8px", marginBottom: "4px" }}>
                <Button variant="default" onClick={() => window.open("/manual", "_blank")}>マニュアル</Button>
                <Button variant="outline" onClick={() => window.open("/contact", "_blank")} title="お問い合わせフォームへ">コンタクト</Button>
              </div>

              {/* Amazon連携 / ログアウト */}
              <div style={{ display: "flex", gap: "8px" }}>
                {isAmazonConnected ? (
                  <Button variant="orange" disabled>✅ 連携済み</Button>
                ) : (
                  <Button
                    variant="orange"
                    onClick={() => {
                      const token = localStorage.getItem("token");
                      if (!token) return alert("ログインが必要です");
                      window.location.href = `${process.env.REACT_APP_API_URL}/api/oauth/start?token=${token}`;
                    }}
                  >
                    Amazon連携
                  </Button>
                )}
                <Button
                  variant="danger"
                  onClick={() => {
                    localStorage.removeItem("token");
                    window.location.href = "/login";
                  }}
                >
                  ログアウト
                </Button>
              </div>

              {user?.amazonAccountName && (
                <div
                  style={{
                    marginTop: "4px",
                    fontWeight: "bold",
                    fontSize: "14px",
                    maxWidth: "300px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={user.amazonAccountName}
                >
                  {user.amazonAccountName}
                </div>
              )}
            </div>
          </div>
        </FadeIn>

        {/* フォームエリア */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2000,
            background: "#fff",
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
          }}
        >
          <FadeIn delay={120}>
            {editing ? (
              <>
                <h2 style={{ margin: "8px 0" }}>編集モード</h2>
                <ScheduleForm
                  onSubmit={handleUpdate}
                  initialData={editing}
                  campaigns={campaigns}
                  onCancel={() => setEditing(null)}
                />
              </>
            ) : (
              <>
                <h2 style={{ margin: "8px 0" }}>新規登録</h2>
                <ScheduleForm onSubmit={handleCreate} campaigns={campaigns} />
              </>
            )}
          </FadeIn>
        </div>
        
        {/* 一覧ボックス */}
        <FadeIn delay={200}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f5f9fc",
              borderRadius: "6px",
              border: "1px solid #e0e7ef",
            }}
          >
            <h2 style={{ marginTop: "4px", marginBottom: "4px" }}>一覧</h2>
            <ScheduleTable
              schedules={schedules}
              campaigns={campaigns}
              onEdit={setEditing}
              onDelete={handleDelete}
              onToggleEnabled={handleToggleEnabled}
            />
          </div>
        </FadeIn>

        {/* NOTE: ScrollToTopButton は position:fixed。
                親に transform を入れると “親相対で固定” になってしまうため
                FadeIn ラッパーは外す */}
        <ScrollToTopButton />
      </div>
    </FadeIn>
  );
};

export default AdminPanel;